import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { DripCampaignInstance, DripCampaignEvent } from '@app/_models/drip-campaign';
import { SharedService } from '@app/_components/_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class DripCampaignService {

  private readonly base = `${environment.apiUrl}/drip/instances`;

  constructor(
    private http: HttpClient,
    private sharedService: SharedService
  ) {}

  /**
   * Find the active drip instance for a given entity (taskSource + taskSourceId).
   * Returns null when no instance exists rather than throwing.
   */
  findInstanceForEntity(taskSource: number, taskSourceId: number): Observable<DripCampaignInstance | null> {
    const params = new HttpParams()
      .set('taskSource', taskSource)
      .set('taskSourceId', taskSourceId)
      .set('size', '1');
    return this.http.get<any>(this.base, { params }).pipe(
      map((res: any) => {
        const list: DripCampaignInstance[] = res?.data?.dripCampaignInstances ?? [];
        return list.length > 0 ? list[0] : null;
      }),
      catchError(() => of(null))
    );
  }

  getInstance(instanceId: number): Observable<DripCampaignInstance> {
    return this.http.get<any>(`${this.base}/${instanceId}`).pipe(
      map((res: any) => res?.data?.dripCampaignInstance as DripCampaignInstance)
    );
  }

  pauseInstance(instanceId: number): Observable<void> {
    return this.http.patch<any>(this.base, {
      dripCampaignInstanceId: instanceId,
      status: 'PAUSED'
    }).pipe(map(() => void 0));
  }

  resumeInstance(instanceId: number): Observable<void> {
    return this.http.patch<any>(this.base, {
      dripCampaignInstanceId: instanceId,
      status: 'ACTIVE'
    }).pipe(map(() => void 0));
  }

  cancelInstance(instanceId: number, reason: string): Observable<void> {
    return this.http.patch<any>(this.base, {
      dripCampaignInstanceId: instanceId,
      status: 'CANCELLED',
      mergeData: reason
    }).pipe(map(() => void 0));
  }

  listInstances(page: number, size: number, sortField: any, sortOrder: any, multiSortMeta: any, globalFilter: any, filters: any): Observable<any> {
    const params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortField, sortOrder, multiSortMeta, globalFilter, filters);
    return this.http.get<any>(this.base, { params });
  }

  getEvents(instanceId: number): Observable<DripCampaignEvent[]> {
    return this.http.get<any>(`${this.base}/${instanceId}`).pipe(
      map((res: any) => (res?.data?.dripCampaignInstance?.events ?? []) as DripCampaignEvent[])
    );
  }
}
